import { useContext } from 'react'

import {
  Button,
  Form,
  ModalContext,
  SidebarModal,
  SidebarNoticeBox,
} from '@domain-group/suit-theme'
import useAgency from '@hooks/useAgency'
import useConversationContainer from '@hooks/useConversationContainer'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { AgencyModel } from '@models/types'

const MODAL_KEY = 'ManageAgencySandboxStatusModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateAgency?: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageAgencySandboxStatusModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateAgency } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = ((modalData as Record<string, unknown>)?.[MODAL_KEY] as {
    agency?: Partial<AgencyModel>,
  }) || {}
  const { agency: initAgency } = modalPayload

  const {
    agency,
    callbacks: { updateAgency: updateFn },
    updating,
  } = useAgency(initAgency)

  const conversationContainerPayload = useConversationContainer({
    // @ts-expect-error TS2739
    conversation: { subject_id: agency?.id, subject_type: 'Agency' },
  })

  const {
    callbacks: { createCommentWithConversation },
    commentForm,
  } = conversationContainerPayload

  const {
    entityState,
    entityState: { body },
    handlers,
  } = commentForm

  // @ts-expect-error
  const isSandbox = !!agency?.sandbox
  const saveEnabled = !!body

  const saveButtonText = isSandbox ? 'Set as Live Agency' : 'Set as Test Agency'

  const updateAgencyPayload = {
    callbacks: {
      action: updateFn,
      afterAction: () => {
        createCommentWithConversation(entityState)
        closeModal()
      },
    },
    entityParams: {
      sandbox: !isSandbox,
    },
    toastText: 'Agency Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title="Test"
        titleSecondLine="Agency"
      />

      <SidebarModal.Content>
        <SidebarNoticeBox>
          <SidebarNoticeBox.Title>
            {isSandbox ? 'This will disable test mode on this agency' : 'This will flag the agency as test mode'}
          </SidebarNoticeBox.Title>

          <SidebarNoticeBox.Paragraph>
            Once an agency is set as a Test Agency, it will be excluded from some reports.
            {' '}
            Please add a note to describe the reason for this change.
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        <Form>
          <Form.Field label="* Reason">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="body"
              style={{ height: 120, resize: 'vertical' }}
              value={body}
              {...handlers}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => updateAgency?.(updateAgencyPayload)}
          size="large"
        >
          {saveEnabled ? saveButtonText : 'Complete Reason'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageAgencySandboxStatusModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
